import axios, { AxiosError, AxiosResponse } from "axios";
import * as moment from "moment";

import {
    MedicalOutcomeModel,
    MedicalOutcome,
    ReviewHealthSurveillanceResultsModel,
    parseReviewHealthSurveillanceResultsModel,
    MedicalTypeId
} from "@teamprevent/common/healthSurveillance/models";
import { TaskModel } from "@teamprevent/common/task/taskActions/models";
import { ServerModelWithDateStrings } from "@teamprevent/common/utils";

import { AddTaskFormModel, parseTaskModel, ReviewHealthSurveillanceResultsSaveModel, MedicalOutcomes, TaskModels } from "../../task/model";

 interface HealthSurveillanceTaskSubmitModel {
    taskType: string;
    taskCode: string | undefined;
    // Local date time
    dueDate: Date | undefined;
 }

 interface HealthSurveillanceOutcomeSubmitModel {
    outcome: string;
    medicalTypeId: MedicalTypeId;
    clinicalNotes: string;
    customerNotes: string;
    recallYears?: number;
    recallWeeks?: number;
 }

 interface ReviewHealthSurveillanceSubmitModel {
    tasks: HealthSurveillanceTaskSubmitModel[];
    outcomes: HealthSurveillanceOutcomeSubmitModel[];
 }

const submitHealthSurveillanceReview = async (ohCaseId: string, taskId: string, outcomes: MedicalOutcomeModel[], tasks: AddTaskFormModel[]) => {
    const result = await axios.post<
        ServerModelWithDateStrings<TaskModel>[],
        AxiosResponse<ServerModelWithDateStrings<TaskModel>[]>,
        ServerModelWithDateStrings<ReviewHealthSurveillanceSubmitModel>
    >(
        `/api/ohCase/${ohCaseId}/task/${taskId}/reviewHealthSurveillanceResults`, {
            tasks: tasks.map(task => ({
                ...task,
                taskType: task.taskType.type,
                taskCode: task.taskCode,
                dueDate: task.dueDate?.format(moment.HTML5_FMT.DATETIME_LOCAL_SECONDS)
            })),
            outcomes: outcomes.map(o => ({
                ...o,
                outcome: (Object.keys(MedicalOutcome) as (keyof typeof MedicalOutcome)[]).find(k => MedicalOutcome[k] === o.outcome)!
            }))
        });
    return { success: result.status === 200, tasks: result.data.map(parseTaskModel) };
};

const getReviewHealthSurveillanceResults = async (customerId: string, ohCaseId: string) => {
    const result = await axios.get<ReviewHealthSurveillanceResultsModel>(
        `/api/customer/${customerId}/ohCase/${ohCaseId}/getReviewHealthSurveillanceResults`);
    return parseReviewHealthSurveillanceResultsModel(result.data);
};

const saveHealthSurveillanceReview = async (
    ohCaseId: string,
    taskId: string,
    outcomes: MedicalOutcomes,
    tasks: TaskModels,
    eTag: string) => {
    try {
        const response = await axios.post<ReviewHealthSurveillanceResultsSaveModel>(
            `/api/ohCase/${ohCaseId}/task/${taskId}/reviewHealthSurveillanceResults/save`, {
                outcomesData: { outcomes },
                tasksData: { tasks },
                eTag
            });
        return response.data;
    } catch (ex) {
        const axiosError = ex as AxiosError;
        if (axiosError.response?.status === 400) {
            const error = { message: "Could not save as a newer version exists", status: 400 };
            throw error;
        } else {
            throw ex;
        }
    }
};

const getSavedHealthSurveillanceReview = async (ohCaseId: string, taskId: string) => {
    const result = await axios.get<ReviewHealthSurveillanceResultsSaveModel>(
        `/api/ohCase/${ohCaseId}/task/${taskId}/getHealthSurveillanceReview`);
    return result.data;
};

export const healthSurveillanceReviewApi = {
    submitHealthSurveillanceReview,
    getReviewHealthSurveillanceResults,
    saveHealthSurveillanceReview,
    getSavedHealthSurveillanceReview
};
